import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { BellIcon, Check, CheckCheck, ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
  type Notification,
  type NotificationCategory,
} from "@/lib/api/notifications";

// ----- Helpers -----

const CATEGORY_BADGE_VARIANT: Record<
  NotificationCategory,
  "secondary" | "success" | "warning" | "error"
> = {
  general: "secondary",
  booking: "secondary",
  payment: "success",
  provider: "warning",
  system: "error",
  promotion: "secondary",
};

function NotificationItemSkeleton() {
  return (
    <div className="flex items-start gap-3 p-3 border-b last:border-0">
      <div className="h-2 w-2 mt-2 bg-muted animate-pulse rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
        <div className="h-3 w-full bg-muted animate-pulse rounded" />
        <div className="h-3 w-1/4 bg-muted animate-pulse rounded" />
      </div>
    </div>
  );
}

// ----- Main Component -----

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch unread count - poll every 30 seconds
  const { data: unreadData } = useQuery({
    queryKey: ["notifications-unread-count"],
    queryFn: fetchUnreadCount,
    refetchInterval: 30000,
  });

  // Fetch recent notifications when dropdown is open
  const {
    data: notificationsData,
    isLoading: notificationsLoading,
  } = useQuery({
    queryKey: ["notifications-recent"],
    queryFn: () => fetchNotifications(1),
    enabled: open,
  });

  // Mark single notification as read
  const markReadMutation = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-unread-count"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-recent"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-inbox"] });
    },
  });

  // Mark all as read
  const markAllReadMutation = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-unread-count"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-recent"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-inbox"] });
    },
  });

  const unreadCount = unreadData?.count ?? 0;
  const notifications = notificationsData?.results?.slice(0, 10) ?? [];

  function handleNotificationClick(notification: Notification) {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id);
    }
    if (notification.action_url) {
      setOpen(false);
      navigate(notification.action_url);
    }
  }

  function handleMarkAllRead() {
    markAllReadMutation.mutate();
  }

  function handleViewAll() {
    setOpen(false);
    navigate("/notifications");
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative"
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
        >
          <BellIcon className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[380px] p-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              onClick={handleMarkAllRead}
              disabled={markAllReadMutation.isPending}
            >
              <CheckCheck className="mr-1 h-3 w-3" />
              Mark all as read
            </Button>
          )}
        </div>

        {/* Notification List */}
        <div className="max-h-[400px] overflow-y-auto">
          {notificationsLoading ? (
            <>
              {Array.from({ length: 4 }).map((_, i) => (
                <NotificationItemSkeleton key={i} />
              ))}
            </>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <BellIcon className="h-8 w-8 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                No notifications yet
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <button
                key={notification.id}
                type="button"
                className={cn(
                  "flex w-full items-start gap-3 border-b px-4 py-3 text-left transition-colors hover:bg-muted/50 last:border-0",
                  !notification.is_read && "bg-info/5"
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                {/* Unread indicator */}
                <div className="mt-1.5 shrink-0">
                  {!notification.is_read ? (
                    <div className="h-2 w-2 rounded-full bg-info" />
                  ) : (
                    <Check className="h-3 w-3 text-muted-foreground/40" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <p
                      className={cn(
                        "text-sm truncate",
                        !notification.is_read ? "font-semibold" : "font-medium"
                      )}
                    >
                      {notification.title}
                    </p>
                    <Badge
                      variant={CATEGORY_BADGE_VARIANT[notification.category]}
                      className="shrink-0 text-[10px] px-1.5 py-0"
                    >
                      {notification.category}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {notification.message}
                  </p>
                  <p className="text-[11px] text-muted-foreground/70">
                    {formatDistanceToNow(new Date(notification.created_at), {
                      addSuffix: true,
                    })}
                  </p>
                </div>

                {/* Action URL indicator */}
                {notification.action_url && (
                  <ExternalLink className="mt-1 h-3 w-3 shrink-0 text-muted-foreground/40" />
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-muted-foreground hover:text-foreground"
            onClick={handleViewAll}
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
